import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { hasAnyAdminFleet } from "../hooks/useFleetAccess.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";

interface Venue {
  id: string;
  fleet_id: string;
  fleet_name: string;
  name: string;
  slug: string;
  address?: string;
  status: string;
  created_at: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "inactive":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Inactive
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

export function Venues() {
  const { user } = useAuth();
  const { options: fleetOptions } = useFleetOptions();
  const [fleetFilter, setFleetFilter] = useState("");
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [formFleetId, setFormFleetId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const canCreateVenue = user ? hasAnyAdminFleet(user) : false;
  const adminFleetOptions = user
    ? user.is_platform_admin
      ? fleetOptions
      : fleetOptions.filter((f) => user.fleets.some((uf) => uf.id === f.id && uf.role === "admin"))
    : [];

  useEffect(() => {
    if (!formFleetId && adminFleetOptions.length === 1) {
      setFormFleetId(adminFleetOptions[0].id);
    }
  }, [adminFleetOptions, formFleetId]);

  const fetchVenues = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/venues"]["get"]({
        parameters: fleetFilter ? { fleet_id: fleetFilter } : {},
        body: null,
      });
      if (res.code === 200) {
        setVenues(res.body as Venue[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [fleetFilter]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleCreate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!formName.trim() || !formSlug.trim() || !formFleetId) return;

      setSubmitting(true);
      setFormError(null);

      try {
        const res = await apiClient["/api/v1/admin/venues"]["post"]({
          parameters: {},
          body: {
            name: formName.trim(),
            slug: formSlug.trim(),
            fleet_id: formFleetId,
            ...(formAddress.trim() ? { address: formAddress.trim() } : {}),
          },
        });
        if (res.code === 201) {
          setShowForm(false);
          setFormName("");
          setFormSlug("");
          setFormAddress("");
          setFormFleetId(adminFleetOptions.length === 1 ? adminFleetOptions[0].id : "");
          fetchVenues();
        } else {
          const body = res.body as { message?: string };
          setFormError(body.message ?? "Failed to create venue");
        }
      } catch {
        setFormError("Network error");
      } finally {
        setSubmitting(false);
      }
    },
    [formName, formSlug, formAddress, formFleetId, fleetOptions, fetchVenues],
  );

  return (
    <main className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Venues</h1>
        {canCreateVenue && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
          >
            {showForm ? "Cancel" : "Create Venue"}
          </button>
        )}
      </div>

      {fleetOptions.length > 1 && (
        <div className="mb-4">
          <select
            aria-label="Filter by fleet"
            value={fleetFilter}
            onChange={(e) => setFleetFilter(e.target.value)}
            className="bg-surface border border-border rounded px-2 py-1 text-sm"
          >
            <option value="">All fleets</option>
            {fleetOptions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-surface-raised border border-border rounded-lg p-4 mb-6 space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Name *</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="The Red Lion"
                required
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Slug *</label>
              <input
                type="text"
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value)}
                placeholder="the-red-lion"
                required
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Address</label>
              <input
                type="text"
                value={formAddress}
                onChange={(e) => setFormAddress(e.target.value)}
                placeholder="123 High Street, London"
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Fleet *</label>
              <select
                value={formFleetId}
                onChange={(e) => setFormFleetId(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors"
              >
                {adminFleetOptions.length !== 1 && <option value="">Select a fleet…</option>}
                {adminFleetOptions.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {formError && <p className="text-sm text-red-400">{formError}</p>}
          <button
            type="submit"
            disabled={submitting || !formName.trim() || !formSlug.trim() || !formFleetId}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </form>
      )}

      {loading && <p className="text-text-muted text-sm">Loading venues...</p>}

      {!loading && venues.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No venues yet.</p>
          <p className="text-text-muted text-sm mt-1">Create your first venue to get started.</p>
        </div>
      )}

      {!loading && venues.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border">
                <th className="pb-2 text-sm font-medium text-text-muted">Name</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Slug</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Fleet</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Status</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Created</th>
              </tr>
            </thead>
            <tbody>
              {venues.map((v) => (
                <tr key={v.id} className="border-b border-border/50 hover:bg-surface-raised/50">
                  <td className="py-3">
                    <a
                      href={`/venues/${v.slug}`}
                      className="text-text-primary hover:text-accent transition-colors no-underline font-medium"
                    >
                      {v.name}
                    </a>
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-text-secondary font-mono">{v.slug}</span>
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-text-secondary">{v.fleet_name}</span>
                  </td>
                  <td className="py-3">{statusBadge(v.status)}</td>
                  <td className="py-3">
                    <span className="text-sm text-text-muted">{formatDate(v.created_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
